import { Pagination } from './generic';

export interface FundListItem {
  id: string;
  name: string;
  account_id: string;
}

export type FundListStatusFilter = 'active' | 'inactive';

export interface FinanceFundsListParams {
  page: number;
  page_size: number;
  search?: string;
  order_by?: string;
  order?: string;
  local_chapter_id?: string;
  spd_chapter_id?: string;
  status?: FundListStatusFilter;
  category_id?: string;
  type_id?: string;
}

export interface FinanceFundsListResponse {
  data: FundListItem[];
  can_create?: boolean;
  pagination: Pagination;
  fund_category_options?: FundLabelOption[];
  fund_type_options?: FundTypeOption[];
}

export interface CreateFundRequest {
  name: string;
  account_id: string;
  short_name: string;
  can_donate: 0 | 1;
  established_by: string;
  established_date: string;
}

export interface CreateFundResponse {
  id: string;
}

export interface FundLabelOption {
  id: string;
  name: string;
}

export interface FundTypeOption {
  id: string;
  name: string;
  category_id: string;
}

export const FUND_CATEGORY_TYPE_ALLOCATION_ID =
  '435685f4d44b49a48bccc6c161b84aed';

export const FUND_TYPE_OTHER_TRANSFERS_ID = '8d674fa51e1c499d88ad6769b9ea926a';
export const FUND_TYPE_OTHER_ID = '456be14510a24182ac5f42c680345b7d';
export const FUND_TYPE_OR_TRANSFERS_ID = 'dc96106fee40478f9b53239dcfc93449';

/** Statement destination names for "Distribution to <name>" (mirrors Go map). */
export const FUND_TYPE_STATEMENT_DISPLAY_NAMES: Record<string, string> = {
  [FUND_TYPE_OR_TRANSFERS_ID]: 'Oregon Charitable Trust',
  '60e7ad8ec44249c5b4a0b2e0fba47799': 'PCE',
  '96c0bb884f8748778cde3c86ba604a1b': 'PSA',
  '2f04c04ed9ee47fba74443740fad18b4': 'STAR',
  '3f8a594d9c8d48d2a6334c9475c61b7f': 'Undesignated Fund',
  c27420cf8422426db208f7ebdf913f79: 'IPS',
  '0ec69baec133474db4593bac0134891c': 'ELF',
  '769791f451404705aefdbf1056acc43b': 'Cottey College',
  '470b2e68ff89469cbb17cca7302a869e': 'Illinois Home Fund, Inc.',
};

export function fundTypeStatementDisplayName(
  typeId: string | undefined | null
): string | undefined {
  const id = (typeId ?? '').trim();
  if (!id) return undefined;
  return FUND_TYPE_STATEMENT_DISPLAY_NAMES[id];
}

export function distributionToStatementLabel(displayName: string): string {
  return `Distribution to ${displayName.trim()}`;
}

export function fundTypeRequiresCustomName(typeId: string): boolean {
  return (
    typeId === FUND_TYPE_OTHER_TRANSFERS_ID || typeId === FUND_TYPE_OTHER_ID
  );
}

export function fundTypeAllowsMultipleRows(typeId: string): boolean {
  return typeId === FUND_TYPE_OTHER_TRANSFERS_ID;
}

export const FUND_OPTION_A = 'option_a';
export const FUND_OPTION_B = 'option_b';
export const FUND_OPTION_C = 'option_c';
export const FUND_OPTION_CUSTOM = 'custom';

export type FundOptionCode =
  | typeof FUND_OPTION_A
  | typeof FUND_OPTION_B
  | typeof FUND_OPTION_C
  | typeof FUND_OPTION_CUSTOM;

export const FUND_OPTION_LABELS: Record<FundOptionCode, string> = {
  option_a: 'Option A',
  option_b: 'Option B',
  option_c: 'Option C',
  custom: 'Custom',
};

export function fundOptionLabel(option: string): string {
  return FUND_OPTION_LABELS[option as FundOptionCode] || option;
}

export interface FundOptionDTO {
  option: string;
  description?: string;
  start_date: string;
  yearly_allocation?: number | null;
  minimum_fund_balance?: number | null;
  custom_rule_configured: boolean;
}

export interface PatchFundOptionInput {
  option: string;
  description?: string;
  yearly_allocation?: number | null;
  minimum_fund_balance?: number | null;
}

const FUND_SUBTYPES_REQUIRING_NAMED_LIMIT = new Set([
  '416fcf6e95a047f28f26ea642a728019',
  '513c589d396e46a5860fa838b301aab5',
  '5ee6ee48c3ed4b3287c3c3689cadba79',
  '6817ce08fa1c4e5aa254bd6dffba6aa9',
  '8a9ba9f5806945a7a3f8e76e68e8d209',
  '9ae274f4faa54ed4a2d0700556f6365e',
  'aa1fc548073745edb8fd2ea8c3920fef',
  'b58961145903450ab3750fb0757d5c66',
  'c2fc91984581495683bcd7c70f0d151a',
  'e5916f27f795410f9fcfa3bdf7af80d4',
  'f7c9dcece5464a5789af503fdd2bd3c3',
]);

const FUND_SUBTYPES_SPECIAL_RECOGNITION = new Set([
  '0c68885787004203b95fd3944424a9ae',
  '5c9d6cf6bbdc4aeb88cc0bfc45ee38af',
  '95f1e66023c444d0b905c7dadd2c9033',
  'a9dfa4232ef84532b1b4f17eb0ed9492',
  'acff96db190344ba89a274d045ed7ef3',
  'b053d457333c48c69963d455f6d1f44c',
  'b7bab857f4644cde86229caac7643562',
  'b82cb9aab2594b85b78bef013e7e0cb2',
  'd2587affbaaf447c80ae44320b928b36',
  'e695b067ecb14f83aa2baabdc95be664',
  'fbf4a88b1d8d4049aad4e8199d21d47a',
]);

const FUND_SUBTYPES_ANNUAL_TRANSFER = new Set([
  '3f19e025e8ff44a1b6054da91c133a6d',
  '430c475970d649cca00e41320cc20500',
  '52da94edb9774d3a8d8b321ebcefa454',
  '68cbcf42f7c94a57b96f43e8b2f79414',
  '76e6ecf9378444ef97a94b4a380bcefc',
  '87a2ae54c98f4bc486df7602b02da927',
  '8d7f3d34359f4d77b40fa8664ca8d53a',
  'a798e084634e4fd89d717f2164036e91',
  'af694ec6419649328c9c3f3039525b7d',
  'd471adfaf1124a8783e7d36e2566ca60',
  'f844f65ee3614a4fb83a1e9549d7c54a',
]);

export function fundSubtypeRequiresNamedLimit(subtypeId: string): boolean {
  return FUND_SUBTYPES_REQUIRING_NAMED_LIMIT.has(subtypeId);
}

export function fundSubtypeIsSpecialRecognition(subtypeId: string): boolean {
  return FUND_SUBTYPES_SPECIAL_RECOGNITION.has(subtypeId);
}

export const SPECIAL_RECOGNITION_AMOUNT = 500;

export function fundSubtypeIsAnnualTransfer(subtypeId: string): boolean {
  return FUND_SUBTYPES_ANNUAL_TRANSFER.has(subtypeId);
}

export const NAMED_SCHOLARSHIP_LIMIT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export type TransferAllocationMode = 'normal' | 'order';

export const TRANSFER_ALLOCATION_MODE_NORMAL: TransferAllocationMode = 'normal';
export const TRANSFER_ALLOCATION_MODE_ORDER: TransferAllocationMode = 'order';

export interface FundAssignedTypeDTO {
  id?: string;
  type_id: string;
  type_name?: string;
  subtype_id?: string;
  subtype_name?: string;
  percentage: number;
  other_transfer_name?: string;
  amount?: number | null;
  named_limit?: number | null;
  special_recognition_count?: number | null;
  sort_order?: number;
}

export interface FundAssignedTypeInput {
  type_id: string;
  subtype_id?: string | null;
  percentage?: number;
  other_transfer_name?: string;
  amount?: number | null;
  named_limit?: number | null;
  special_recognition_count?: number | null;
  sort_order?: number;
}

export interface FundNamedAmountDTO {
  id: string;
  amount: number;
  type_id: string;
  type_name?: string;
  start_date: string;
  end_date?: string | null;
}

export interface CreateFundNamedAmountRequest {
  amount: number;
  type_id: string;
  start_date: string;
  end_date?: string | null;
}

export interface PatchFundNamedAmountRequest {
  amount?: number;
  type_id?: string;
  start_date?: string;
  end_date?: string | null;
}

export interface FundSubtypeOption {
  id: string;
  name: string;
  type_id: string;
}

export interface FundAttachmentType {
  id: string;
  name: string;
  description?: string;
  sort_order: number;
}

export interface FundAttachmentItem {
  id: string;
  file_name: string;
  description?: string;
  content_type?: string;
  file_size_bytes?: number;
  uploaded_by_name?: string;
  uploaded_at?: string;
  attachment_type_id: string;
}

export interface FundAttachmentGroup {
  type_id: string;
  type_name: string;
  type_sort_order: number;
  attachments: FundAttachmentItem[];
}

export interface FundAttachmentsGroupedResponse {
  groups: FundAttachmentGroup[];
}

export interface PatchFundAttachmentRequest {
  description?: string;
  attachment_type_id?: string;
}

export interface FundNoteDTO {
  id: string;
  note: string;
  created_at?: string;
  created_by_id: string;
  created_by_name?: string;
}

export interface FundPositionDTO {
  id: string;
  user_id: string;
  name: string;
  position: string;
  position_id: string;
  start_date?: string;
  end_date?: string | null;
}

export interface ChairRoleDTO {
  id: string;
  name: string;
}

export interface FundRoleSlotDTO {
  id: string;
  group_role_id: string;
  group_role_name: string;
  ref_group_role_id: string;
  ref_group_role_name: string;
  local_chapter_id?: string;
  local_chapter_name?: string;
  spd_chapter_id?: string;
  spd_chapter_name?: string;
  display_label: string;
  start_date?: string;
  end_date?: string | null;
  is_active: boolean;
}

export interface FundDetailResponse {
  id: string;
  name: string;
  short_name?: string;
  account_id?: string;
  description?: string;
  can_donate: number;
  sponser_chapter_code?: string;
  type_id?: string;
  type_name?: string;
  category_id?: string;
  category_name?: string;
  subtype_id?: string;
  subtype_name?: string;
  established_by?: string;
  established_date?: string | null;
  state_id?: string;
  status_id?: string;
  status_name?: string;
  local_chapter_id?: string;
  local_chapter_name?: string;
  spd_chapter_id?: string;
  spd_chapter_name?: string;
  opt_out: number;
  selection_committee?: string;
  transfer_letter?: string;
  statement_paragraph?: string;
  activity_letter?: string;
  fund_use?: string;
  principal_amount?: number | null;
  current_fund_option?: FundOptionDTO | null;
  notes: FundNoteDTO[];
  positions: FundPositionDTO[];
  group_id?: string | null;
  chair_roles: ChairRoleDTO[];
  chair_role_slots: FundRoleSlotDTO[];
  email_group_id?: string | null;
  email_members: FundPositionDTO[];
  transfer_letter_members?: FundPositionDTO[];
  email_roles: ChairRoleDTO[];
  email_role_slots: FundRoleSlotDTO[];
  ref_role_options: FundLabelOption[];
  fund_type_options: FundTypeOption[];
  fund_category_options: FundLabelOption[];
  fund_subtype_options: FundSubtypeOption[];
  fund_status_options: FundLabelOption[];
  uses_type_allocations?: boolean;
  transfer_allocation_mode?: TransferAllocationMode | string;
  assigned_types?: FundAssignedTypeDTO[];
  is_primary?: number;
  uses_named_amounts?: boolean;
  named_amounts?: FundNamedAmountDTO[];
  can_manage: boolean;
  can_manage_attachments: boolean;
  can_manage_statement_data?: boolean;
}

export interface PatchFundRequest {
  name?: string;
  short_name?: string;
  account_id?: string;
  description?: string;
  can_donate?: number;
  sponser_chapter_code?: string;
  type_id?: string | null;
  /** Set to empty string to clear; omit to leave unchanged. */
  category_id?: string;
  subtype_id?: string | null;
  status_id?: string | null;
  /** Set to empty string to clear; omit to leave unchanged. */
  local_chapter_id?: string;
  /** Set to empty string to clear; omit to leave unchanged. */
  spd_chapter_id?: string;
  established_by?: string;
  established_date?: string | null;
  opt_out?: number;
  selection_committee?: string;
  transfer_letter?: string;
  statement_paragraph?: string;
  activity_letter?: string;
  fund_use?: string;
  principal_amount?: number | null;
  /** YYYY-MM-DD; stamps fund history for tracked field changes. Default today on server if omitted. */
  as_of_date?: string;
  fund_option?: PatchFundOptionInput;
  transfer_allocation_mode?: TransferAllocationMode | string;
  assigned_types?: FundAssignedTypeInput[] | null;
}

export interface CreateFundNoteRequest {
  note: string;
}

export interface PatchFundNoteRequest {
  note: string;
}

export interface CreateFundPositionRequest {
  user_id: string;
  role_id: string;
  start_date: string;
  end_date?: string | null;
  apply_to_other_funds?: boolean;
}

export interface ReplaceFundPositionRequest {
  user_id: string;
  start_date: string;
  end_date?: string | null;
  apply_to_other_funds?: boolean;
}

export interface FundChairRoleHistoryMember {
  user_id: string;
  name: string;
  start_date: string;
  end_date?: string | null;
}

export interface FundChairRoleHistoryResponse {
  future: FundChairRoleHistoryMember[];
  past: FundChairRoleHistoryMember[];
}

export interface FundChairOtherFundDTO {
  fund_id: string;
  fund_name: string;
  role_id: string;
  role_name: string;
  group_member_id: string;
}

export interface FundChairOtherFundsResponse {
  other_funds: FundChairOtherFundDTO[];
}

export interface PatchFundPositionRequest {
  user_id?: string;
  role_id?: string;
  start_date?: string;
  end_date?: string | null;
}

export interface CreateFundRoleSlotRequest {
  group_role_id: string;
  ref_group_role_id: string;
  local_chapter_id?: string | null;
  spd_chapter_id?: string | null;
  start_date: string;
  end_date?: string | null;
}

export interface PatchFundRoleSlotRequest {
  group_role_id?: string;
  ref_group_role_id?: string;
  local_chapter_id?: string | null;
  spd_chapter_id?: string | null;
  start_date?: string;
  end_date?: string | null;
}

export interface CreateFundEmailContactRequest {
  user_id: string;
  start_date: string;
  end_date?: string | null;
}

export interface PatchFundEmailContactRequest {
  user_id?: string;
  start_date?: string;
  end_date?: string | null;
}

export type CreateFundTransferLetterContactRequest =
  CreateFundEmailContactRequest;

export type PatchFundTransferLetterContactRequest =
  PatchFundEmailContactRequest;

export type FundHistoryKind = 'field_change' | 'fund_option' | 'named_amount';

export interface FundHistoryItem {
  kind: FundHistoryKind;
  sort_date: string;
  field_name?: string;
  field_label?: string;
  old_value?: string;
  new_value?: string;
  old_label?: string;
  new_label?: string;
  as_of_date?: string;
  created_at?: string;
  created_by_id?: string;
  created_by_name?: string;
  start_date?: string;
  end_date?: string | null;
  summary?: string;
}

export interface FundHistoryResponse {
  items: FundHistoryItem[];
}

export interface FundStatementDatesResponse {
  dates: string[];
}

export interface FundStatementFieldDTO {
  field_name: string;
  label: string;
  account_number?: string;
  base_value: number | null;
  effective_value: number | null;
  is_overridden: boolean;
}

export interface FundStatementOverrideDTO {
  id: string;
  field_name: string;
  field_label: string;
  old_value?: string;
  new_value?: string;
  reason?: string;
  created_by_id?: string;
  created_by_name?: string;
  created_at?: string;
}

export interface FundStatementCalculation {
  computed_ending: number;
  balance_ok: boolean;
  balance_diff: number;
  option_code?: string;
  option_label?: string;
  all_contributions?: number | null;
  prior_principal_retained?: number | null;
  principal_retained?: number | null;
  stored_principal_retained?: number | null;
  missing_prior_principal?: boolean;
  prior_principal_assumed?: boolean;
  prior_principal_assumed_zero?: boolean;
  distribution_minimum_blocked?: boolean;
  distribution_minimum_required?: number;
  principal_retained_mismatch?: boolean;
  principal_retained_needs_save?: boolean;
  principal_retained_is_seed?: boolean;
  surplus?: number | null;
  principal_amount?: number | null;
  yearly_allocation?: number | null;
  can_award_predetermined?: boolean;
  available_income?: number | null;
  available_income_label?: string;
  custom_add_back?: number | null;
  pre_add_back_available_income?: number | null;
  custom_unrestricted_amount?: number | null;
  /** When set, value to save differs from displayed principal_retained (e.g. remainder-to-principal). */
  principal_retained_to_persist?: number | null;
  custom_not_implemented?: boolean;
  custom_message?: string;
}

export interface FundStatementDataResponse {
  mip_data_id: string;
  as_of_date: string;
  previous_balance_date?: string | null;
  ending_balance_date?: string | null;
  fields: FundStatementFieldDTO[];
  overrides: FundStatementOverrideDTO[];
  calculation?: FundStatementCalculation | null;
  fund_name?: string;
  account_id?: string;
  type_id?: string;
  type_name?: string;
  category_id?: string;
  category_name?: string;
  description?: string;
  already_allocated?: number;
  already_allocated_award_year?: string;
  available_for_scholarships?: number | null;
  scholarship_allocation?: FundYearlyScholarshipAllocationDTO | null;
  prior_distributions?: FundStatementPriorDistributionDTO[];
  final_available_for_distribution?: number | null;
  transfer_allocations?: FundYearlyTransferAllocationDTO[];
  transfer_allocation_mode?: string;
  is_statement_locked?: boolean;
  statement_lock_year?: number;
  statement_lock_file_name?: string;
}

export interface FundStatementLockResponse {
  year: number;
  file_name: string;
  replaced: boolean;
  as_of_date: string;
}

export interface FundYearlyScholarshipAllocationDTO {
  id?: string;
  fund_id?: string;
  fund_mip_data_id?: string;
  available_for_scholarships: number;
  already_allocated: number;
  amount_to_award: number;
}

export interface FundStatementPriorDistributionDTO {
  distributed_on: string;
  amount: number;
  label: string;
}

export interface FundYearlyTransferAllocationDTO {
  id?: string;
  type_id?: string;
  type_name?: string;
  subtype_id?: string;
  subtype_name?: string;
  custom_name?: string;
  destination_fund_id?: string;
  destination_fund_name?: string;
  calculated_distribution: number;
  actual_distribution: number;
  accrued_distribution: number;
  last_years_calculated_accrued_distribution: number;
  total_distributions: number;
  total_accrued_distributions: number;
}

export interface PostFundStatementOverrideRequest {
  as_of_date: string;
  field_name: string;
  new_value?: number | null;
  reason?: string;
  clear?: boolean;
}
